import type { Comp, GameObj, PosComp } from "kaplay";
import { POINTER, TOUCH } from "../config/controls";
import { k } from "../core/kaplay";
import { isTouchDevice, settings } from "../core/settings";
import { viewport } from "../core/viewport";
import { readTouch } from "../ui/touchControls";
import {
    NO_KEYS, NO_POINTER, applyStick, buildIntent, mergeKeys,
    type InputDevice, type InputIntent, type KeyboardSample, type PointerSample,
} from "../systems/inputMapping";

export interface PlayerInputComp extends Comp {
    intent: InputIntent;
    device: InputDevice;
}

function readKeyboard(): KeyboardSample {
    return {
        left: k.isButtonDown("left"),
        right: k.isButtonDown("right"),
        pedal: k.isButtonDown("pedal"),
        brake: k.isButtonDown("brake"),
        jump: k.isButtonPressed("jump"),
        boost: k.isButtonPressed("boost"),
        bell: k.isButtonPressed("bell"),
        pause: k.isButtonPressed("pause"),
        restart: k.isButtonPressed("restart"),
    };
}

function readPointer(riderPos: PosComp["pos"]): PointerSample {
    const offset = k.mousePos().sub(k.toScreen(riderPos)).scale(1 / viewport.uiScale);
    return {
        dx: offset.x,
        dy: offset.y,
        clicked: k.isMousePressed("left"),
    };
}

function chosenDevice(): InputDevice {
    return isTouchDevice() ? "touch" : settings.desktopControls;
}

export function playerInput(): PlayerInputComp {
    return {
        id: "playerInput",
        require: ["pos"],
        device: chosenDevice(),
        intent: buildIntent(chosenDevice(), NO_KEYS, NO_POINTER, POINTER),

        update(this: GameObj<PlayerInputComp | PosComp>) {
            if (this.device !== "touch") {
                this.intent = buildIntent(this.device, readKeyboard(), readPointer(this.pos), POINTER);
                return;
            }
            const touch = readTouch();
            const keys = mergeKeys(readKeyboard(), { ...NO_KEYS, jump: touch.jump, pause: touch.pause });
            this.intent = applyStick(buildIntent(this.device, keys, readPointer(this.pos), POINTER), touch.stick, TOUCH);
        },
    };
}
